/**
 * Web 页面客户端桥接兼容层。
 *
 * 统一 FinNex Promise 协议和 WPF/CefSharp 回调协议，对业务侧始终返回 Promise。
 */
export type ClientBridgeParams = unknown;

export interface ClientBridgeCallOptions {
  /** WPF 环境下是否跳过 API.use，直接调用 window 上的桥接方法 */
  direct?: boolean;
}

export interface FinnexClientBridgeInfo {
  /** 当前 FinNex 桥接对象是否可用 */
  isAvailable: boolean;
  /** FinNex 桥接协议版本 */
  protocolVersion?: string;
}

export interface ClientBridge {
  /** 统一调用客户端方法，始终返回 Promise */
  call<T = unknown>(method: string, options: ClientBridgeCallOptions): Promise<T>;
  call<T = unknown>(
    method: string,
    params?: ClientBridgeParams,
    options?: ClientBridgeCallOptions,
  ): Promise<T>;
  /** 获取 FinNex 桥接对象信息 */
  getFinnexClientBridgeInfo(): FinnexClientBridgeInfo;
  /** 当前是否运行在 FinNex 客户端 */
  readonly isFinNex: boolean;
  /** 当前是否运行在 WPF/CefSharp 客户端 */
  readonly isWpf: boolean;
  /** 当前是否运行在任意客户端容器 */
  readonly isClient: boolean;
}

interface FinnexBridge {
  protocolVersion?: string;
  call?: (method: string, params: ClientBridgeParams | null) => Promise<unknown> | unknown;
}

interface ApiUseOptions {
  method: string;
  data: ClientBridgeParams | null;
  success: (result?: unknown) => void;
  error: (code?: unknown, msg?: unknown) => void;
  notClient: () => void;
}

interface ClientBridgeGlobal extends Window {
  __finnexBridge?: FinnexBridge;
  CefExternal?: unknown;
  Util?: Record<string, unknown>;
  FuturesChainsExtra?: Record<string, unknown>;
  API?: {
    use?: (options: ApiUseOptions) => void;
  };
  ClientBridge?: ClientBridge;
}

/**
 * 获取当前浏览器全局对象，SSR 或无 window 环境下返回 undefined。
 */
function getGlobal(): ClientBridgeGlobal | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window as unknown as ClientBridgeGlobal;
}

/**
 * 从全局对象上安全读取 WPF 桥接命名空间。
 */
function getNamespace(global: ClientBridgeGlobal, nsName: string): Record<string, unknown> | undefined {
  const namespace = (global as unknown as Record<string, unknown>)[nsName];
  if (typeof namespace !== 'object' || namespace === null) {
    return undefined;
  }
  return namespace as Record<string, unknown>;
}

/**
 * 按旧 WPF 方法签名调用桥接方法，保留无参和单参调用差异。
 */
function callBridgeMethod(
  namespace: Record<string, unknown>,
  bridgeMethod: (...args: unknown[]) => unknown,
  params?: ClientBridgeParams,
): unknown {
  if (params === undefined) {
    return bridgeMethod.call(namespace);
  }
  return bridgeMethod.call(namespace, params);
}

/**
 * 判断第二个参数是否为 ClientBridge.call 的配置对象。
 */
function isClientBridgeCallOptions(params: ClientBridgeParams): params is ClientBridgeCallOptions {
  return (
    typeof params === 'object' &&
    params !== null &&
    !Array.isArray(params) &&
    typeof (params as ClientBridgeCallOptions).direct === 'boolean'
  );
}

/**
 * 解析 ClientBridge.call 的重载参数。
 */
function normalizeCallArgs(
  paramsOrOptions?: ClientBridgeParams | ClientBridgeCallOptions,
  options?: ClientBridgeCallOptions,
): {
  params?: ClientBridgeParams;
  options?: ClientBridgeCallOptions;
} {
  if (options === undefined && isClientBridgeCallOptions(paramsOrOptions)) {
    return {
      options: paramsOrOptions,
    };
  }

  return {
    params: paramsOrOptions,
    options,
  };
}

/**
 * 将客户端 error 回调参数归一成可读错误信息。
 */
function getErrorMessage(code?: unknown, msg?: unknown): string {
  if (msg) {
    return String(msg);
  }
  if (code) {
    return String(code);
  }
  return 'ClientBridge: 客户端调用失败';
}

/**
 * 构造 FinNex 桥接对象的调试信息。
 */
function buildFinnexClientBridgeInfo(
  bridge?: FinnexBridge,
  isAvailable = false,
): FinnexClientBridgeInfo {
  if (!bridge || typeof bridge.protocolVersion === 'undefined') {
    return { isAvailable };
  }
  return {
    isAvailable,
    protocolVersion: bridge.protocolVersion,
  };
}

/**
 * 判断当前是否处于 FinNex 客户端环境。
 */
function isFinNexClient(global = getGlobal()): boolean {
  return Boolean(global?.__finnexBridge && typeof global.__finnexBridge.call === 'function');
}

/**
 * 判断当前是否处于 WPF/CefSharp 客户端环境。
 */
function isWpfClient(global = getGlobal()): boolean {
  if (!global || isFinNexClient(global)) {
    return false;
  }

  return Boolean(
    global.CefExternal ||
      (global.Util && typeof global.Util.getHxVer === 'function'),
  );
}

/**
 * 通过 FinNex Promise 协议调用客户端方法。
 */
function callFinNex<T>(global: ClientBridgeGlobal, method: string, params?: ClientBridgeParams): Promise<T> {
  const bridgeCall = global.__finnexBridge?.call;
  if (typeof bridgeCall !== 'function') {
    return Promise.reject(new Error('ClientBridge: FinNex 桥接对象不可用'));
  }

  return Promise.resolve(bridgeCall(method, params == null ? null : params)) as Promise<T>;
}

/**
 * 通过 WPF API.use 协议调用客户端方法。
 */
function callWpfViaApiUse<T>(
  global: ClientBridgeGlobal,
  method: string,
  params?: ClientBridgeParams,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    try {
      global.API?.use?.({
        method,
        data: params == null ? null : params,
        success: (result?: unknown) => {
          resolve((result != null ? result : null) as T);
        },
        error: (code?: unknown, msg?: unknown) => {
          reject(new Error(getErrorMessage(code, msg)));
        },
        notClient: () => {
          reject(new Error('ClientBridge: 当前不在客户端环境中'));
        },
      });
    } catch {
      reject(new Error('ClientBridge: API.use 调用失败'));
    }
  });
}

/**
 * 直接调用 WPF 全局命名空间上的桥接方法。
 */
function callWpfDirect<T>(
  global: ClientBridgeGlobal,
  method: string,
  params?: ClientBridgeParams,
): Promise<T> {
  const dotIndex = method.indexOf('.');
  const nsName = dotIndex >= 0 ? method.slice(0, dotIndex) : method;
  const fnName = dotIndex >= 0 ? method.slice(dotIndex + 1) : method;
  const namespace = getNamespace(global, nsName);
  const bridgeMethod = namespace?.[fnName];

  if (!namespace || typeof bridgeMethod !== 'function') {
    return Promise.reject(new Error(`ClientBridge: WPF 方法不可用：${method}`));
  }
  const wpfBridgeMethod = bridgeMethod as (...args: unknown[]) => unknown;

  return new Promise<T>((resolve, reject) => {
    try {
      const syncResult = callBridgeMethod(namespace, wpfBridgeMethod, params);

      if (syncResult !== undefined) {
        resolve(syncResult as T);
        return;
      }

      setTimeout(() => {
        resolve(null as T);
      }, 0);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 根据 WPF 方法特性选择 API.use 或直连调用。
 */
function callWpf<T>(
  global: ClientBridgeGlobal,
  method: string,
  params?: ClientBridgeParams,
  options?: ClientBridgeCallOptions,
): Promise<T> {
  if (
    options?.direct === true ||
    !global.API ||
    typeof global.API.use !== 'function'
  ) {
    return callWpfDirect<T>(global, method, params);
  }

  return callWpfViaApiUse<T>(global, method, params);
}

/**
 * 统一调用客户端方法。
 *
 * @param method 客户端方法名，例如 `Util.getSkinName` 或 `external.closeWebDlg`
 * @param params 调用参数，未传时会按客户端协议处理，WPF 直连时不传参
 */
export function callClientBridge<T = unknown>(
  method: string,
  options: ClientBridgeCallOptions,
): Promise<T>;
export function callClientBridge<T = unknown>(
  method: string,
  params?: ClientBridgeParams,
  options?: ClientBridgeCallOptions,
): Promise<T>;
export function callClientBridge<T = unknown>(
  method: string,
  paramsOrOptions?: ClientBridgeParams | ClientBridgeCallOptions,
  callOptions?: ClientBridgeCallOptions,
): Promise<T> {
  const global = getGlobal();
  const { params, options } = normalizeCallArgs(paramsOrOptions, callOptions);

  if (global && isFinNexClient(global)) {
    return callFinNex<T>(global, method, params);
  }

  if (global && isWpfClient(global)) {
    return callWpf<T>(global, method, params, options);
  }

  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn('[ClientBridge] 当前不在客户端环境中，调用已忽略：', method);
  }
  return Promise.reject(new Error('ClientBridge: 当前无可用客户端'));
}

/**
 * 获取 FinNex 桥接对象信息。
 */
function getFinnexClientBridgeInfo(): FinnexClientBridgeInfo {
  const global = getGlobal();
  const bridge = global?.__finnexBridge;

  return buildFinnexClientBridgeInfo(bridge, typeof bridge?.call === 'function');
}

export const ClientBridge: ClientBridge = {
  call: callClientBridge,
  getFinnexClientBridgeInfo,
  /** 当前是否运行在 FinNex 客户端 */
  get isFinNex() {
    return isFinNexClient();
  },
  /** 当前是否运行在 WPF/CefSharp 客户端 */
  get isWpf() {
    return isWpfClient();
  },
  /** 当前是否运行在任意客户端容器 */
  get isClient() {
    return this.isFinNex || this.isWpf;
  },
};

/**
 * 将 ClientBridge 挂载到全局对象，便于旧页面通过 `window.ClientBridge` 使用。
 *
 * @param target 要挂载的全局对象，默认使用当前 window
 */
export function installClientBridge(target: ClientBridgeGlobal | undefined = getGlobal()): ClientBridge {
  if (target) {
    target.ClientBridge = ClientBridge;
  }
  return ClientBridge;
}
